import { useCallback } from 'react';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useDropzone } from 'react-dropzone';
import { Box, Stack, Typography } from '@mui/material';

interface DropzoneFieldProps {
  label: string;
  value: File | null;
  onDrop: (file: File) => void;
}

export const DropzoneField = ({ label, value, onDrop }: DropzoneFieldProps) => {
  const MAX_FILE_SIZE = 2 * 1024 * 1024; // 2MB
  const handleDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (acceptedFiles.length > 0) {
        onDrop(acceptedFiles[0]);
      }
    },
    [onDrop]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: handleDrop,
    multiple: false,
    maxSize: MAX_FILE_SIZE,
    accept: {
      'image/*': [],
      'application/pdf': [],
    },
  });

  return (
    <Box
      {...getRootProps()}
      sx={{
        border: '2px dashed',
        borderColor: value ? '' : isDragActive ? 'primary.main' : 'grey.400',
        borderRadius: 3,
        p: 3,
        textAlign: 'center',
        cursor: 'pointer',
        transition: '0.3s',
        // backgroundColor: value && "success.light" : "grey.50",
        '&:hover': {
          borderColor: 'primary.main',
          backgroundColor: 'grey.100',
        },
      }}
    >
      <input {...getInputProps()} />

      <Stack spacing={1} alignItems="center">
        {value ? (
          <>
            <CheckCircleIcon color="success" />
            <Typography fontWeight={600}>{label}</Typography>
            <Typography variant="body2" color="text.secondary">
              {value.name}
            </Typography>
          </>
        ) : (
          <>
            <CloudUploadIcon sx={{ color: '#03395ae0' }} />
            <Typography fontWeight={600}>{label}</Typography>
            <Typography variant="body2" color="text.secondary">
              Drag & drop or click to upload
            </Typography>
          </>
        )}
      </Stack>
    </Box>
  );
};
